import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import PersonAvatar from "@/components/PersonAvatar";
import StaffLeavePanel from "@/components/StaffLeavePanel";
import PhotoUploader from "@/components/PhotoUploader";
import StatusPill from "@/components/StatusPill";
import PayrollConfigCard from "@/components/PayrollConfigCard";
import MedicationCompetencyCard from "@/components/medication_competency/MedicationCompetencyCard";
import {
  ArrowLeft, Mail, Phone, MapPin, Calendar as CalendarIcon, Briefcase, ShieldCheck, IdCard,
  GraduationCap, CalendarClock, Car, PillBottle, FileCheck2, AlertTriangle, Plus, Trash2,
  Pencil, RefreshCw,
} from "lucide-react";

const HR_KINDS = [
  { value: "right_to_work", label: "Right to Work" },
  { value: "dbs", label: "DBS Check" },
  { value: "passport", label: "Passport" },
  { value: "visa", label: "Visa" },
  { value: "driving_licence", label: "Driving Licence" },
  { value: "training_cert", label: "Training Certificate" },
  { value: "other", label: "Other" },
];

const EXPIRY_STYLES = {
  valid: "bg-emerald-50 text-emerald-800 border-emerald-300",
  expiring_soon: "bg-amber-50 text-amber-800 border-amber-300",
  expired: "bg-red-50 text-red-800 border-red-300",
  no_expiry: "bg-slate-50 text-slate-700 border-slate-300",
  missing: "bg-slate-100 text-slate-500 border-slate-300",
};

const EXPIRY_LABELS = {
  valid: "Valid",
  expiring_soon: "Expiring soon",
  expired: "Expired",
  no_expiry: "No expiry",
  missing: "Missing",
};

export default function StaffProfile() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docOpen, setDocOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docForm, setDocForm] = useState({ kind: "right_to_work", ref_number: "", issued_on: "", expires_on: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/staff/${staffId}`);
      setDossier(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const openNewDoc = () => {
    setEditingDoc(null);
    setDocForm({ kind: "right_to_work", ref_number: "", issued_on: "", expires_on: "", notes: "" });
    setDocOpen(true);
  };
  const openEditDoc = (d) => {
    setEditingDoc(d);
    setDocForm({
      kind: d.kind || "right_to_work",
      ref_number: d.ref_number || "",
      issued_on: d.issued_on || "",
      expires_on: d.expires_on || "",
      notes: d.notes || "",
    });
    setDocOpen(true);
  };
  const saveDoc = async () => {
    setSaving(true);
    try {
      if (editingDoc) {
        await api.patch(`/staff/${staffId}/hr-documents/${editingDoc.id}`, docForm);
      } else {
        await api.post(`/staff/${staffId}/hr-documents`, docForm);
      }
      toast.success(editingDoc ? "Document updated" : "Document added");
      setDocOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const deleteDoc = async (d) => {
    if (!window.confirm(`Delete this ${d.kind_label} record?`)) return;
    try {
      await api.delete(`/staff/${staffId}/hr-documents/${d.id}`);
      toast.success("Document removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBadge = async (field, next) => {
    try {
      await api.patch(`/staff/${staffId}/badges`, { [field]: next });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center text-sm text-slate-500">Loading staff profile…</div>;
  if (!dossier) return <div className="flex-1 flex items-center justify-center text-sm text-red-600">Staff member not found. <Link className="ml-2 text-sky-700 underline" to="/users">Back to Staff</Link></div>;

  const { user, hr_documents, training, badges, next_shift, site_name, manager_name } = dossier;

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Header bar */}
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/users")} className="text-slate-500 hover:text-slate-900" data-testid="btn-back-staff-list" title="Back to Staff list">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">People</div>
            <h1 className="font-heading text-base font-semibold" data-testid="staff-profile-page-title">Staff Profile</h1>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="h-9" data-testid="btn-refresh-staff-profile"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
      </div>

      {/* Mobile header — Batch 7C */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate("/users")} className="text-slate-500 hover:text-slate-900 min-h-[44px] min-w-[44px] flex items-center justify-center -ml-2" data-testid="btn-back-staff-list-mobile" title="Back to Staff list">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">People</div>
            <h1 className="font-heading text-base font-semibold truncate" data-testid="staff-profile-page-title-mobile">Staff Profile</h1>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="h-11 shrink-0" data-testid="btn-refresh-staff-profile-mobile"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="flex-1 overflow-auto p-4 lg:p-6 space-y-4 bg-slate-50">
        {/* Hero card */}
        <div className="bg-white border border-slate-200 rounded-md p-4 lg:p-6 flex flex-col sm:flex-row items-start gap-4 sm:gap-6" data-testid="staff-hero-card">
          <PhotoUploader kind="staff" targetId={staffId} person={user} size="2xl" onUpdated={load} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-slate-900" data-testid="staff-name-header">
                  {user.preferred_name || user.name}
                  {user.preferred_name && user.name !== user.preferred_name && (
                    <span className="ml-2 text-sm text-slate-400 font-normal">({user.name})</span>
                  )}
                </h2>
                <div className="text-sm text-slate-600 mt-0.5">
                  {user.job_title || <span className="text-slate-400">No job title</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill value={user.role} kind="generic" />
                {user.available_for_rota !== false && <span className="pill pill-ok">Available for rota</span>}
                {badges.driver && <span className="pill pill-info inline-flex items-center gap-1"><Car className="w-3 h-3" /> Driver</span>}
                {badges.medication_competent && <span className="pill pill-info inline-flex items-center gap-1"><PillBottle className="w-3 h-3" /> Med Competent</span>}
                <ExpiryChip state={badges.dbs_state} label="DBS" />
                <ExpiryChip state={badges.rtw_state} label="RTW" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 mt-4 text-sm">
              <MetaLine icon={Mail} label="Email" value={user.email} testid="meta-email" />
              <MetaLine icon={Phone} label="Phone" value={user.phone} testid="meta-phone" />
              <MetaLine icon={MapPin} label="Site" value={site_name || (user.site_id ? "Unknown site" : "All sites")} testid="meta-site" />
              <MetaLine icon={Briefcase} label="Employment" value={(user.employment_type || "").replace("_", " ") || "—"} testid="meta-employment" />
              <MetaLine icon={CalendarIcon} label="Start Date" value={user.start_date || user.created_at?.slice(0, 10)} testid="meta-start" />
              <MetaLine icon={IdCard} label="Line Manager" value={manager_name || "—"} testid="meta-manager" />
            </div>
          </div>
        </div>

        {/* Three-up stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={GraduationCap}
            testid="stat-training"
            label="Training Compliance"
            value={`${training.percentage}%`}
            sub={`${training.valid_courses.length}/${training.expected_courses.length} core courses valid`}
            tone={training.percentage >= 90 ? "ok" : training.percentage >= 70 ? "info" : "warn"}
          />
          <StatCard
            icon={CalendarClock}
            testid="stat-next-shift"
            label="Next Shift"
            value={next_shift ? new Date(next_shift.scheduled_start).toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" }) : "None scheduled"}
            sub={next_shift ? `${new Date(next_shift.scheduled_start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}${next_shift.scheduled_end ? " – " + new Date(next_shift.scheduled_end).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : ""}${next_shift.client_name ? " · " + next_shift.client_name : ""}` : "Assign a shift on the rota"}
            tone={next_shift ? "info" : "muted"}
          />
          <StatCard
            icon={FileCheck2}
            testid="stat-docs"
            label="HR Documents"
            value={String((hr_documents || []).length)}
            sub={(hr_documents || []).filter((d) => d.expiry_state === "expired").length + " expired · " + (hr_documents || []).filter((d) => d.expiry_state === "expiring_soon").length + " expiring"}
            tone={
              (hr_documents || []).some((d) => d.expiry_state === "expired") ? "warn" :
              (hr_documents || []).some((d) => d.expiry_state === "expiring_soon") ? "info" : "ok"
            }
          />
        </div>

        {/* Badges toggler */}
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Capabilities</div>
          <div className="flex items-center gap-2 flex-wrap">
            <BadgeToggle icon={Car} label="Driver" active={badges.driver} onToggle={() => toggleBadge("driver", !badges.driver)} testid="badge-driver" />
            <span className="text-[11px] text-slate-400 mx-2">Medication competency is derived from Training records</span>
          </div>
        </div>

        {/* Right-to-Work / Documents */}
        <div className="bg-white border border-slate-200 rounded-md" data-testid="hr-docs-section">
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Compliance</div>
              <h3 className="font-heading text-sm font-semibold">Right-to-Work &amp; HR Documents</h3>
            </div>
            <Button size="sm" onClick={openNewDoc} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-add-hr-doc">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Document
            </Button>
          </div>
          {(hr_documents || []).length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="hr-docs-empty">
              <AlertTriangle className="w-6 h-6 mx-auto text-amber-400 mb-2" />
              No Right-to-Work or DBS records on file yet. Add the first document to stay CQC-compliant.
            </div>
          ) : (
            <table className="data-table" data-testid="hr-docs-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th className="w-20 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hr_documents.map((d) => (
                  <tr key={d.id} data-testid={`hr-doc-${d.id}`}>
                    <td className="font-medium">{d.kind_label}</td>
                    <td className="mono text-xs">{d.ref_number || <span className="text-slate-300">—</span>}</td>
                    <td className="text-xs">{d.issued_on || <span className="text-slate-300">—</span>}</td>
                    <td className="text-xs">{d.expires_on || <span className="text-slate-300">—</span>}</td>
                    <td><ExpiryChip state={d.expiry_state} /></td>
                    <td className="text-xs text-slate-600 max-w-xs truncate" title={d.notes || ""}>{d.notes || <span className="text-slate-300">—</span>}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => openEditDoc(d)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`edit-hr-${d.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteDoc(d)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`delete-hr-${d.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Medication Competency (sub-module of Supervision & Appraisals) */}
        <MedicationCompetencyCard staffId={staffId} staffName={user.name || user.email} canManage={true} />

        {/* Payroll config (Manager/Admin only edit; Care Worker sees read-only summary
            or nothing if server returns 403). */}
        <PayrollConfigCard staffId={staffId} canManage={true} />

        {/* Leave & Holidays panel */}
        <StaffLeavePanel staffUserId={staffId} />
      </div>

      {/* Add/Edit HR document dialog */}
      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="hr-doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingDoc ? "Edit document" : "Add HR document"}</DialogTitle>
            <DialogDescription>Track Right-to-Work, DBS, passport, visa or other compliance documents with issue and expiry dates.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium">Type *</Label>
              <Select value={docForm.kind} onValueChange={(v) => setDocForm({ ...docForm, kind: v })}>
                <SelectTrigger className="mt-1 h-9 bg-white" data-testid="hr-doc-kind"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {HR_KINDS.map((k) => <SelectItem key={k.value} value={k.value} data-testid={`hr-kind-${k.value}`}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Reference Number</Label>
              <Input value={docForm.ref_number} onChange={(e) => setDocForm({ ...docForm, ref_number: e.target.value })} placeholder="e.g. DBS-001234567" className="mt-1 h-9 mono text-xs" data-testid="hr-doc-ref" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Issued</Label>
                <Input type="date" value={docForm.issued_on} onChange={(e) => setDocForm({ ...docForm, issued_on: e.target.value })} className="mt-1 h-9" data-testid="hr-doc-issued" />
              </div>
              <div>
                <Label className="text-xs font-medium">Expires</Label>
                <Input type="date" value={docForm.expires_on} onChange={(e) => setDocForm({ ...docForm, expires_on: e.target.value })} className="mt-1 h-9" data-testid="hr-doc-expires" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Notes</Label>
              <Textarea value={docForm.notes} onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })} rows={2} className="mt-1 text-sm" data-testid="hr-doc-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocOpen(false)} data-testid="hr-doc-cancel">Cancel</Button>
            <Button onClick={saveDoc} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="hr-doc-save">
              {saving ? "Saving…" : editingDoc ? "Save changes" : "Add document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaLine({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2 text-slate-700" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-20 shrink-0">{label}</span>
      <span className="truncate" title={value || ""}>{value || <span className="text-slate-400">—</span>}</span>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone = "info", testid }) {
  const tones = {
    ok: "border-emerald-200 bg-emerald-50 text-emerald-900",
    info: "border-sky-200 bg-sky-50 text-sky-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    muted: "border-slate-200 bg-white text-slate-800",
  };
  return (
    <div className={`border rounded-md p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-75 font-semibold flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-2xl font-heading font-semibold mt-1 tabular-nums">{value}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}

function BadgeToggle({ icon: Icon, label, active, onToggle, testid }) {
  return (
    <button
      type="button" onClick={onToggle} data-testid={testid}
      className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        active ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      <span className={`ml-1 w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-300"}`} />
    </button>
  );
}

function ExpiryChip({ state, label }) {
  const cls = EXPIRY_STYLES[state] || EXPIRY_STYLES.missing;
  const text = label ? `${label} · ${EXPIRY_LABELS[state] || state}` : EXPIRY_LABELS[state] || state;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${cls}`}>{text}</span>;
}
